import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAdmin } from '../../context/AdminContext';
import { useTheme } from '../../context/ThemeContext';
import AnimatedStats from '../../components/admin/dashboard/AnimatedStats';
import NetworkVisualization from '../../components/admin/dashboard/NetworkVisualization';
import RevenueChart from '../../components/admin/dashboard/RevenueChart';
import ActivityFeed from '../../components/admin/dashboard/ActivityFeed';
import GradientCard from '../../components/admin/common/GradientCard';
import { TrendingUp, Users, DollarSign, Activity } from 'lucide-react';

const Dashboard = () => {
  const { t } = useTranslation();
  const { adminStats, realtimeData } = useAdmin();
  const { isDarkMode } = useTheme();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30
      }
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className={`text-4xl font-bold bg-gradient-to-r ${isDarkMode ? 'from-gray-100 to-gray-300' : 'from-gray-900 to-gray-600'} bg-clip-text text-transparent`}>
            {t('admin.dashboard')}
          </h1>
          <p className={`mt-2 text-lg ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {t('dashboard.welcomeMessage')} {t('common.to').toLowerCase()} {t('admin.systemSettings').toLowerCase()} {t('common.of')} {t('common.your')} {t('common.business')}
          </p>
        </div>
        <motion.div 
          className="mt-4 lg:mt-0 flex items-center space-x-3"
          whileHover={{ scale: 1.02 }}
        >
          <div className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-2xl text-sm font-medium shadow-lg">
            {t('admin.systemHealth')}
          </div>
          <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {new Date().toLocaleDateString('es-ES', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </div>
        </motion.div>
      </motion.div>

      {/* Estadísticas principales */}
      <motion.div variants={itemVariants}>
        <AnimatedStats stats={adminStats} />
      </motion.div>

      {/* Grid principal */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Columna izquierda - Gráficos */}
        <div className="lg:col-span-2 space-y-8">
          <motion.div variants={itemVariants}>
            <RevenueChart />
          </motion.div>
          
          <motion.div variants={itemVariants}>
            <NetworkVisualization />
          </motion.div>
        </div>

        {/* Columna derecha - Métricas y Actividad */}
        <div className="space-y-8">
          <motion.div variants={itemVariants}>
            <GradientCard
              title={t('admin.systemHealth')}
              icon={<Activity className="w-6 h-6" />}
              gradient="from-blue-500 to-cyan-500"
            >
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('admin.cpuUsage')}</span>
                  <span className={`text-sm font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                    {Math.round(realtimeData.systemLoad || 0)}%
                  </span>
                </div>
                <div className={`w-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded-full h-2`}>
                  <div
                    className="bg-gradient-to-r from-orange-500 to-orange-600 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${realtimeData.systemLoad || 0}%` }}
                  ></div>
                </div>

                <div className="flex justify-between items-center">
                  <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('admin.memory')}</span>
                  <span className={`text-sm font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                    {Math.round(realtimeData.memoryUsage || 0)}%
                  </span>
                </div>
                <div className={`w-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded-full h-2`}>
                  <div
                    className="bg-gradient-to-r from-green-500 to-emerald-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${realtimeData.memoryUsage || 0}%` }}
                  ></div>
                </div>
              </div>
            </GradientCard>
          </motion.div>

          <motion.div variants={itemVariants}>
            <ActivityFeed />
          </motion.div>
        </div>
      </div>

      {/* Métricas rápidas */}
      <motion.div 
        variants={itemVariants}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        <GradientCard
          title={t('dashboard.monthlyGrowth')}
          value={`+${adminStats.monthlyGrowth || 0}%`}
          icon={<TrendingUp className="w-6 h-6" />}
          gradient="from-green-500 to-emerald-500"
        />

        <GradientCard
          title={t('admin.newUsers')}
          value={adminStats.newUsersToday || 0}
          icon={<Users className="w-6 h-6" />}
          gradient="from-blue-500 to-cyan-500"
        />

        <GradientCard
          title={t('admin.avgCommission')}
          value={`$${adminStats.avgCommission || 0}`}
          icon={<DollarSign className="w-6 h-6" />}
          gradient="from-purple-500 to-pink-500"
        />

        <GradientCard
          title={t('admin.conversionRate')}
          value={`${adminStats.conversionRate || 0}%`}
          icon={<Activity className="w-6 h-6" />}
          gradient="from-orange-500 to-red-500"
        />
      </motion.div>
    </motion.div>
  );
};

export default Dashboard;